import { useState } from 'react'
import { FileText } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Lightbox } from '@/components/ui/Lightbox'
import type { SMSMedia } from '@/types/sms'

interface MediaItemProps {
  media: SMSMedia
  isOutbound: boolean
  darkMode: boolean
  onImageClick: (url: string) => void
}

/** Un média d'un texto : image cliquable, vidéo lisible, ou fichier à ouvrir. */
export function MediaItem({ media, isOutbound, darkMode, onImageClick }: MediaItemProps) {
  if (media.content_type.startsWith('image/')) {
    return (
      <button
        type="button"
        onClick={() => onImageClick(media.url)}
        className="block focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-400 rounded-xl"
      >
        <img
          src={media.url}
          alt={media.filename}
          className="rounded-xl max-w-[260px] max-h-[260px] object-cover cursor-zoom-in hover:opacity-90 transition-opacity"
        />
      </button>
    )
  }
  if (media.content_type.startsWith('video/')) {
    return (
      <video
        src={media.url}
        controls
        className="rounded-xl max-w-[260px] max-h-[200px]"
      />
    )
  }
  return (
    <a
      href={media.url}
      target="_blank"
      rel="noopener noreferrer"
      className={cn(
        'flex items-center gap-2 px-3 py-2 rounded-xl text-xs transition-colors',
        isOutbound
          ? 'bg-indigo-700/60 text-white hover:bg-indigo-700/80'
          : darkMode
            ? 'bg-slate-700/60 text-slate-200 hover:bg-slate-700/80'
            : 'bg-gray-100 text-gray-700 hover:bg-gray-200',
      )}
    >
      <FileText className="w-4 h-4 flex-shrink-0" />
      <span className="truncate max-w-[180px]">{media.filename}</span>
    </a>
  )
}

interface MediaListProps {
  media: SMSMedia[]
  isOutbound: boolean
  darkMode: boolean
}

/** Les médias d'un texto, avec leur propre lightbox (fil de conversation). */
export function SMSMediaList({ media, isOutbound, darkMode }: MediaListProps) {
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null)
  if (media.length === 0) return null
  return (
    <>
      <div
        className={cn(
          'mb-1 flex flex-col gap-1',
          isOutbound ? 'items-end' : 'items-start',
        )}
      >
        {media.map((m, idx) => (
          <MediaItem
            key={idx}
            media={m}
            isOutbound={isOutbound}
            darkMode={darkMode}
            onImageClick={setLightboxUrl}
          />
        ))}
      </div>
      <Lightbox url={lightboxUrl} onClose={() => setLightboxUrl(null)} />
    </>
  )
}
